import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

export const useBlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const postsPerPage = 9;

  const fetchPosts = async (page = 1, category = null) => {
    try {
      setLoading(true);
      let query = supabase
        .from('blog_posts')
        .select('*', { count: 'exact' })
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (category) {
        query = query.eq('category', category);
      }

      const { data, count, error } = await query
        .range((page - 1) * postsPerPage, page * postsPerPage - 1);

      if (error) throw error;

      setPosts(data);
      setTotalPages(Math.ceil(count / postsPerPage));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(page, selectedCategory);
  }, [page, selectedCategory]);

  return { posts, loading, error, page, totalPages, setPage, setSelectedCategory };
};

export const useBlogPost = (slug) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewIncremented, setViewIncremented] = useState(false);

  useEffect(() => {
    if (slug) fetchPost(slug);
  }, [slug]);

  // Increment view count once per session per post
  useEffect(() => {
    if (post && slug && !viewIncremented) {
      incrementViewCount(slug);
    }
  }, [post, slug, viewIncremented]);

  const fetchPost = async (postSlug) => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', postSlug)
        .eq('status', 'published')
        .single();

      if (error) throw error;

      setPost(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async (postSlug) => {
    // Check if already viewed in this session
    const viewedPosts = JSON.parse(sessionStorage.getItem('viewedPosts') || '[]');
    
    if (viewedPosts.includes(postSlug)) {
      setViewIncremented(true);
      return; // Already viewed in this session
    }

    try {
      // Call the database function to increment view
      const { error } = await supabase.rpc('increment_view_count', { 
        post_slug: postSlug 
      });

      if (error) {
        console.error('Error incrementing view:', error);
        return;
      }

      // Mark as viewed in session
      viewedPosts.push(postSlug);
      sessionStorage.setItem('viewedPosts', JSON.stringify(viewedPosts));
      setViewIncremented(true);

      // Update local post state with new view count
      setPost(prev => prev ? { ...prev, view_count: (prev.view_count || 0) + 1 } : prev);
    } catch (err) {
      console.error('Error incrementing view count:', err);
    }
  };

  return { post, loading, error };
};
